import { Component, Input, Output, EventEmitter, forwardRef, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface YearRange {
  startYear: number | null;
  endYear: number | null;
}

@Component({
  selector: 'app-year-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './year-range-picker.component.html',
  styleUrl: './year-range-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => YearRangePickerComponent),
      multi: true
    }
  ]
})
export class YearRangePickerComponent implements ControlValueAccessor {
  @Input() placeholder: string = 'Choose Year Range';
  @Input() styleClass: string = '';
  @Input() disabled: boolean = false;
  @Input() showIcon: boolean = true;
  @Input() minYear: number = 1900;
  @Input() maxYear: number = new Date().getFullYear();

  @Output() onYearRangeChange = new EventEmitter<YearRange>();

  yearRange: YearRange = { startYear: null, endYear: null };
  isOpen = false;
  // Start from 2000 to show more previous years by default
  currentDecade: number = Math.max(2000, Math.floor((new Date().getFullYear() - 20) / 20) * 20);
  selectedStartYear: number | null = null;
  selectedEndYear: number | null = null;
  hoverYear: number | null = null;
  
  private onChange = (value: YearRange | null) => {};
  private onTouched = () => {};

  constructor(private elementRef: ElementRef) {
    document.addEventListener('click', (e: MouseEvent) => this.onDocumentClick(e), true);
  }

  /** Capture phase so sibling controls still receive outside-close when bubble is stopped upstream. */
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  get displayValue(): string {
    if (this.yearRange.startYear && this.yearRange.endYear) {
      return `${this.yearRange.startYear} - ${this.yearRange.endYear}`;
    } else if (this.yearRange.startYear) {
      return `${this.yearRange.startYear} - ...`;
    }
    return '';
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.onTouched();
        // Initialize selection state
        this.selectedStartYear = this.yearRange.startYear;
        this.selectedEndYear = this.yearRange.endYear;
      }
    }
  }

  getYearsInDecade(): number[] {
    const years: number[] = [];
    // Show 20 years at a time (5 rows x 4 columns)
    for (let i = 0; i < 20; i++) {
      const year = this.currentDecade + i;
      if (year >= this.minYear && year <= this.maxYear) {
        years.push(year);
      }
    }
    return years;
  }

  getDecadeRange(): string {
    const start = this.currentDecade;
    const end = Math.min(this.currentDecade + 19, this.maxYear);
    return `${start} - ${end}`;
  }

  isYearInRange(year: number): boolean {
    // Check preview mode (hovering)
    if (this.selectedStartYear !== null && this.hoverYear !== null && this.selectedEndYear === null) {
      const start = this.selectedStartYear;
      const end = this.hoverYear;
      return year >= Math.min(start, end) && year <= Math.max(start, end);
    }
    
    // Check complete range
    if (this.selectedStartYear !== null && this.selectedEndYear !== null) {
      return year >= this.selectedStartYear && year <= this.selectedEndYear;
    }
    
    return false;
  }

  isStartYear(year: number): boolean {
    return this.selectedStartYear !== null && year === this.selectedStartYear;
  }

  isEndYear(year: number): boolean {
    return this.selectedEndYear !== null && year === this.selectedEndYear;
  }

  isInRangeButNotStartOrEnd(year: number): boolean {
    // Check if we're in preview mode (start selected, hovering for end)
    if (this.selectedStartYear !== null && this.hoverYear !== null && this.selectedEndYear === null) {
      const start = this.selectedStartYear;
      const end = this.hoverYear;
      return year > Math.min(start, end) && year < Math.max(start, end);
    }
    
    // Check if we have a complete range
    if (this.selectedStartYear !== null && this.selectedEndYear !== null) {
      return year > this.selectedStartYear && year < this.selectedEndYear;
    }
    
    return false;
  }

  selectYear(year: number): void {
    if (year < this.minYear || year > this.maxYear) return;
    
    if (this.selectedStartYear === null || (this.selectedStartYear !== null && this.selectedEndYear !== null)) {
      // Start new selection
      this.selectedStartYear = year;
      this.selectedEndYear = null;
      this.hoverYear = null;
      
      // Update the model with just start year
      this.yearRange = {
        startYear: this.selectedStartYear,
        endYear: null
      };
      this.onChange(this.yearRange);
    } else if (this.selectedStartYear !== null && this.selectedEndYear === null) {
      // Complete the range
      if (year < this.selectedStartYear) {
        // If selected year is before start, swap them
        this.selectedEndYear = this.selectedStartYear;
        this.selectedStartYear = year;
      } else {
        this.selectedEndYear = year;
      }
      
      // Update the model
      this.yearRange = {
        startYear: this.selectedStartYear,
        endYear: this.selectedEndYear
      };
      this.onChange(this.yearRange);
      this.onYearRangeChange.emit(this.yearRange);
      this.isOpen = false;
    }
  }

  onYearHover(year: number): void {
    if (this.selectedStartYear !== null && this.selectedEndYear === null) {
      this.hoverYear = year;
    }
  }

  onYearLeave(): void {
    this.hoverYear = null;
  }

  previousDecade(): void {
    const newDecade = this.currentDecade - 20;
    if (newDecade >= this.minYear - (this.minYear % 20)) {
      this.currentDecade = newDecade;
    } else {
      this.currentDecade = Math.floor(this.minYear / 20) * 20;
    }
  }

  nextDecade(): void {
    const newDecade = this.currentDecade + 20;
    if (newDecade <= this.maxYear) {
      this.currentDecade = newDecade;
    }
  }

  clear(): void {
    this.yearRange = { startYear: null, endYear: null };
    this.selectedStartYear = null;
    this.selectedEndYear = null;
    this.hoverYear = null;
    this.onChange(null);
    this.onYearRangeChange.emit({ startYear: null, endYear: null });
    this.onTouched();
  }

  getYearClasses(year: number): string {
    const baseClasses = 'w-16 h-10 flex items-center justify-center text-sm font-medium transition-all cursor-pointer relative border-0 outline-none focus:outline-none rounded-lg';
    
    if (year < this.minYear || year > this.maxYear) {
      return `${baseClasses} text-gray-300 cursor-not-allowed opacity-50 bg-transparent`;
    }
    
    const isStart = this.isStartYear(year);
    const isEnd = this.isEndYear(year);
    const isInRange = this.isInRangeButNotStartOrEnd(year);
    const isCurrentYear = year === new Date().getFullYear();
    
    // Start year - dark green
    if (isStart) {
      return `${baseClasses} bg-teal-700 text-white font-semibold z-10`;
    }
    
    // End year - dark green
    if (isEnd) {
      return `${baseClasses} bg-teal-700 text-white font-semibold z-10`;
    }
    
    // Years in range (between start and end) - light green background
    if (isInRange) {
      return `${baseClasses} bg-green-100 text-gray-900 font-normal`;
    }
    
    // Current year - light green circular (only if not selected or in range)
    if (isCurrentYear && !isStart && !isEnd && !isInRange) {
      return `${baseClasses} bg-green-300 text-gray-900 font-semibold`;
    }
    
    // Regular years
    return `${baseClasses} text-gray-900 hover:bg-green-50 bg-transparent`;
  }

  writeValue(value: YearRange | null): void {
    if (value && value.startYear !== null && value.endYear !== null) {
      this.yearRange = {
        startYear: value.startYear,
        endYear: value.endYear
      };
      this.selectedStartYear = value.startYear;
      this.selectedEndYear = value.endYear;
      this.currentDecade = Math.floor(value.startYear / 20) * 20;
    } else if (value && value.startYear !== null) {
      this.yearRange = {
        startYear: value.startYear,
        endYear: null
      };
      this.selectedStartYear = value.startYear;
      this.selectedEndYear = null;
      this.currentDecade = Math.floor(value.startYear / 20) * 20;
    } else {
      this.yearRange = { startYear: null, endYear: null };
      this.selectedStartYear = null;
      this.selectedEndYear = null;
      // Reset to default decade when clearing (show more previous years)
      this.currentDecade = Math.max(2000, Math.floor((new Date().getFullYear() - 20) / 20) * 20);
    }
  }

  registerOnChange(fn: (value: YearRange | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

